import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {Timeline} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline/timeline';
import {updateTimelineBottomBarHeight} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {
  BOTTOM_BAR_MAX_HEIGHT,
  POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import {PlayButton} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/play-button/play-button';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import styles from './poster-editor-web-bottom-bar.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {EDITOR_SIDEBAR_WIDTH} from '@Components/poster-editor/library/poster-editor-library';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';
import {TimelineZoomOptions} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-zoom-options/timeline-zoom-options';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {updateIsTimelineModeActive} from '@Components/poster-editor/poster-editor-reducer';

export function PosterEditorWebBottomBar(): ReactElement<any> | null {
  const isVideoOrHasMultiPages = useIsVideoOrHasMultiPages();
  const timelineContentHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.timelineContentHeight;
  });
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });
  const dispatch = useAppDispatch();
  const bottomShadowState = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomShadowState;
  });
  const isSidebarOpen = useAppSelector((state) => {
    return state.posterEditor.isSidebarOpen;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });

  useEffect((): void => {
    if (bottomBarRef.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_WEB_BOTTOM_BAR] = $(bottomBarRef.current);
    }
  }, [bottomBarRef.current]);

  useEffect((): void => {
    updateHeightForWebBottomBarInRedux();

    window.posterEditor?.whiteboard?.scaling.fitToScreenIfFitAlready();
  }, [timelineContentHeight]);

  const updateHeightForWebBottomBarInRedux = (): void => {
    if (!bottomBarRef.current || !timelineContentHeight) {
      return;
    }
    const height = timelineContentHeight + 24 + 56;
    dispatch(updateTimelineBottomBarHeight(height <= BOTTOM_BAR_MAX_HEIGHT ? height : BOTTOM_BAR_MAX_HEIGHT));
  };

  const getTimelineContent = (): ReactElement<any> | null => {
    return <Timeline />;
  };

  const getCloseIcon = (): ReactElement | null => {
    if (!bottomBarRef?.current) {
      return null;
    }

    const boundingClient = bottomBarRef.current.getBoundingClientRect();
    const left = (boundingClient.left + boundingClient.right) / 2 + 22;

    return (
      <div
        style={{
          top: `calc(${boundingClient.top}px - 60px - 15px)`,
          left: left,
        }}
        onClick={(): void => {
          dispatch(updateIsTimelineModeActive(false));
        }}
        className={`_position-absolute cursor-pointer flex-center ${styles.closeIcon}`}
      >
        <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const getHeightForTimelineContent = (): string => {
    return `${bottomBarHeight}px`;
  };

  const getWidthForWebBottomBar = (): string => {
    const paddingOnSides = 12;
    if (isSidebarSmall && !isSidebarOpen) {
      const collapsedContextualMenuWidth = 22;
      return `calc(100% - ${paddingOnSides * 2}px - ${collapsedContextualMenuWidth}px)`;
    }
    return `calc(100% - ${paddingOnSides * 3}px - ${EDITOR_SIDEBAR_WIDTH}px)`;
  };

  return (
    <>
      {getCloseIcon()}
      <PosterEditorErrorBoundary>
        {isVideoOrHasMultiPages ? (
          <div
            ref={bottomBarRef}
            style={{
              height: getHeightForTimelineContent(),
              width: getWidthForWebBottomBar(),
            }}
            id={POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID}
            className={`flex-v-row flex-justify-end shadow-static-elevation-2 radius-12 border-s-standard _overflow-hidden ${styles.container}  ${isPickingModeEnabled ? styles.pickingMode : ''}`}
          >
            {getTimelineContent()}
            <div className={`flex flex-row-justify-between flex-items-center _full-width spacing-p-2 ${styles.bottomContent} ${bottomShadowState ? styles.addTopShadow : ''}`}>
              <PlayButton />
              <TimelineZoomOptions />
            </div>
          </div>
        ) : null}
      </PosterEditorErrorBoundary>
    </>
  );
}
